import React, { Fragment, useState, useContext } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Grid2";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import Alert from "../../layout/Alert";
import ResponsiveContentWrapper from "../../layout/ResponsiveContentWrapper";
import { connect } from "react-redux";
import { addMfaDevice } from "../../../action/auth";
import { setAlert } from "../../../action/alert";
import ChangePassword from "./changePassword";
import { UserContext } from "../../routing/Routes";
import AppButton from "../../Common/Button/Button";
import Dialog from "../../Common/Dailog/Dailog";
import FormInput from "../../Common/FormInputs/MuiTextField";
import env from "../../../env";
import { generateUpdateProfileOtp, validateUpdateProfileOtp } from "../../../action/password";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const Settings = ({
  wrapperClass,
  addMfaDevice,
  setAlert: showAlert,
  tenantInfo,
  generateUpdateProfileOtp: sendUpdateProfileOtp,
  validateUpdateProfileOtp: confirmUpdateProfileOtp,
}) => {
  const userInfo = useContext(UserContext);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [verifyType, setVerifyType] = useState("");
  const [verifyValue, setVerifyValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpFlowLoading, setOtpFlowLoading] = useState(false);
  const handleOpenChangePassword = (event) => {
    event?.currentTarget?.blur?.();
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleOpenConfirmationDialog = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setOpenConfirmationDialog(true);
  };

  const handleCloseConfirmationDialog = () => {
    setOpenConfirmationDialog(false);
  };

  const handleOpenChangeDeviceDialog = () => {
    addMfaDevice();
    handleCloseConfirmationDialog();
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };

  const getDisplayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const getContactValue = (value) => {
    return value === null || value === undefined || value === "" ? "Not added" : String(value);
  };

  const renderContactValue = (value, isVerified) => {
    const contact = getContactValue(value);
    const hasValue = value !== null && value !== undefined && value !== "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Typography variant="body2">{contact}</Typography>
        {hasValue && isVerified && (
          <CheckCircleRoundedIcon
            sx={{ color: "success.main", fontSize: 18 }}
            titleAccess="Verified"
          />
        )}
      </Box>
    );
  };

  const renderRoleChips = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return <Typography variant="body2">-</Typography>;
    }

    return (
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {roles.map((role) => (
          <Chip
            key={String(role)}
            label={String(role)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
    );
  };

  const extractErrorMessage = (err) => {
    if (!err) return "Something went wrong. Please try again.";
    if (typeof err === "string") return err;
    if (err.response?.data) {
      const d = err.response.data;
      if (typeof d === "string") return d;
      if (d.message) return d.message;
      if (d.error) return d.error;
    }
    return err.message || "Something went wrong. Please try again.";
  };

  const fetchUserProfile = async (force = false) => {
    if (!force && (profileData || profileLoading)) {
      return;
    }
    const silent = Boolean(force && profileData);
    try {
      if (!silent) {
        setProfileLoading(true);
      }
      const iamURL = env.GET_IAM_API_URL();
      const tenantId = tenantInfo?.tenantId || "panasonic";
      const config = {
        headers: {
          "Content-Type": "application/json",
          Tenantid: tenantId,
        },
      };
      const response = await axios.get(`${iamURL}/api/identity/v1/user/profile`, config);
      setProfileData(response?.data || {});
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch user profile";
      showAlert(msg, "danger");
    } finally {
      if (!silent) {
        setProfileLoading(false);
      }
    }
  };

  const openVerificationPopup = (type, value) => {
    if (!value) {
      showAlert(`No ${type} available to verify`, "danger");
      return;
    }
    setVerifyType(type);
    setVerifyValue(value);
    setOtp("");
    setOtpSent(false);
    setOpenVerifyDialog(true);
  };

  const closeVerificationPopup = () => {
    setOpenVerifyDialog(false);
    setVerifyType("");
    setVerifyValue("");
    setOtpSent(false);
    setOtp("");
  };

  const handleSendOtp = async () => {
    const raw = (verifyValue || "").trim();
    if (!raw) {
      showAlert("Please enter a valid value.", "danger");
      return;
    }
    try {
      setOtpFlowLoading(true);
      const payload =
        verifyType === "mobile" ? { mobile: raw } : { email: raw };
      await sendUpdateProfileOtp(payload);
      setOtpSent(true);
      setOtp("");
      showAlert("OTP sent successfully.", "success");
    } catch (err) {
      showAlert(extractErrorMessage(err), "danger");
    } finally {
      setOtpFlowLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const value = (verifyValue || "").trim();
    const code = (otp || "").trim();
    if (!value) {
      showAlert("Please enter a valid value.", "danger");
      return;
    }
    if (!code) {
      showAlert("Please enter the OTP.", "danger");
      return;
    }
    try {
      setOtpFlowLoading(true);
      const payload =
        verifyType === "mobile"
          ? { mobile: value, otp: code }
          : { email: value, otp: code };
      await confirmUpdateProfileOtp(payload);
      showAlert("Verification completed successfully.", "success");
      await fetchUserProfile(true);
      closeVerificationPopup();
    } catch (err) {
      showAlert(extractErrorMessage(err), "danger");
    } finally {
      setOtpFlowLoading(false);
    }
  };

  return (
    <Fragment>
      <ResponsiveContentWrapper className={wrapperClass || ""}>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          {userInfo.level > 1 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Need to update your password?
                  </Typography>
                </Box>
                <AppButton variant="primary" size="medium" onClick={handleOpenChangePassword} sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
                  Change Password
                </AppButton>
              </Stack>
            </Paper>
          )}
          <ChangePassword open={open} onClose={handleClose} />
          <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Two Step Authentication
              </Typography>
              <AppButton variant="primary" size="medium" onClick={handleOpenConfirmationDialog} sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}>
                Add Device
              </AppButton>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                User Information
              </Typography>
              <AppButton variant="primary" size="medium" onClick={fetchUserProfile} sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}>
                {profileLoading ? "Loading..." : "Profile"}
              </AppButton>
            </Stack>
            {profileData && (
              <Box sx={{ mt: 2 }}>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Username</Typography>
                    <Typography variant="body2">{getDisplayValue(profileData.username)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Mobile</Typography>
                    {renderContactValue(profileData.mobile, profileData.mobileVerified)}
                    {!profileData.mobileVerified && profileData.mobile && (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => openVerificationPopup("mobile", profileData.mobile)}
                        sx={{ fontSize: 12 }}
                      >
                        Verify
                      </Link>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    {renderContactValue(profileData.email, profileData.emailVerified)}
                    {!profileData.emailVerified && profileData.email && (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => openVerificationPopup("email", profileData.email)}
                        sx={{ fontSize: 12 }}
                      >
                        Verify
                      </Link>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">MFA Enabled</Typography>
                    <Typography variant="body2">{getDisplayValue(profileData.mfaEnabled)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Last Logged In</Typography>
                    <Typography variant="body2">{formatDateTime(profileData.lastLoggedInTs)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Creation Date</Typography>
                    <Typography variant="body2">{formatDateTime(profileData.creationDate)}</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">Roles</Typography>
                    {renderRoleChips(profileData.roles)}
                  </Grid2>
                </Grid2>
              </Box>
            )}
          </Paper>
          <Alert />
        </Box>
      </ResponsiveContentWrapper>
      <Dialog
        open={openConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        title="Add Two-Step Authentication Device?"
        maxWidth="sm"
        closeOnBackdropClick
        closeOnEscapeKeyDown
        actions={[
          { label: "Cancel", variant: "ghost", onClick: handleCloseConfirmationDialog },
          { label: "Ok", variant: "primary", onClick: handleOpenChangeDeviceDialog },
        ]}
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to add your Two-Step Authentication device? This cannot be reverted back.
        </Typography>
      </Dialog>
      <Dialog
        open={openVerifyDialog}
        onClose={closeVerificationPopup}
        title={`${verifyType === "mobile" ? "Mobile" : "Email"} Verification`}
        maxWidth="xs"
        fullWidth
        closeOnBackdropClick
        closeOnEscapeKeyDown
        actions={
          !otpSent
            ? [
                {
                  label: "Cancel",
                  variant: "ghost",
                  onClick: closeVerificationPopup,
                  disabled: otpFlowLoading,
                },
                {
                  label: otpFlowLoading ? "Sending..." : "Send OTP",
                  variant: "primary",
                  onClick: handleSendOtp,
                  disabled: otpFlowLoading,
                  loading: otpFlowLoading,
                },
              ]
            : [
                {
                  label: "Cancel",
                  variant: "ghost",
                  onClick: closeVerificationPopup,
                  disabled: otpFlowLoading,
                },
                {
                  label: otpFlowLoading ? "Verifying..." : "Verify OTP",
                  variant: "primary",
                  onClick: handleVerifyOtp,
                  disabled: otpFlowLoading,
                  loading: otpFlowLoading,
                },
              ]
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <FormInput
            label={verifyType === "mobile" ? "Mobile Number" : "Email"}
            name="verifyTarget"
            value={verifyValue}
            onChange={(e) => setVerifyValue(e.target.value)}
            fullWidth
            size="small"
          />
          {otpSent && (
            <FormInput
              label="Enter OTP"
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              size="small"
            />
          )}
        </Box>
      </Dialog>
    </Fragment>
  );
};

Settings.propTypes = {
  addMfaDevice: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  generateUpdateProfileOtp: PropTypes.func.isRequired,
  validateUpdateProfileOtp: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  tenantInfo: state.tenant.tenantInfo,
});

const mapDispatchToProps = (dispatch) => ({
  addMfaDevice: () => dispatch(addMfaDevice()),
  setAlert: (msg, type) => dispatch(setAlert(msg, type)),
  generateUpdateProfileOtp: (payload) => dispatch(generateUpdateProfileOtp(payload)),
  validateUpdateProfileOtp: (payload) => dispatch(validateUpdateProfileOtp(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
